import { pdfMarkdown } from "./visualisations/schema.mjs";

export const PAGE_WIDTH = 515;
const MAX_IMAGE_HEIGHT = 420;
const IMAGE = /!\[([^\]\n]*)\]\(([^)\s]+)\)/g;
const RELATIVE_IMAGE = /^[\w.-]+\.(png|jpe?g|gif|webp)$/i;

export function pdfName(name) {
  const stem = String(name || "").replace(/\.(md|markdown)$/i, "").trim();
  return `${stem || "Report"}.pdf`;
}

/**
 * Swap image links for data URLs, which pdfmake can embed. Images stored next
 * to the file (`chart.png`) are fetched through `resolveImage`; anything else
 * that is not already a data URL is left out of the PDF.
 */
export async function inlineImages(markdown, resolveImage) {
  const source = String(markdown || "");
  let result = "";
  let cursor = 0;
  for (const match of source.matchAll(IMAGE)) {
    result += source.slice(cursor, match.index);
    cursor = match.index + match[0].length;
    const [whole, alt, src] = match;
    if (/^data:image\/(png|jpeg|gif|webp);base64,/i.test(src)) {
      result += whole;
      continue;
    }
    if (!RELATIVE_IMAGE.test(src) || typeof resolveImage !== "function") continue;
    try {
      const data = await resolveImage(src);
      if (typeof data === "string" && data.startsWith("data:image/")) result += `![${alt}](${data})`;
    } catch (error) {
      console.warn("[HelpChat] Could not load an image for the PDF", error);
    }
  }
  return result + source.slice(cursor);
}

/** Scale every image to the page width, keeping tall images on one page. */
export function fitImages(node) {
  if (Array.isArray(node)) {
    node.forEach(fitImages);
    return node;
  }
  if (!node || typeof node !== "object") return node;
  if (typeof node.image === "string") {
    delete node.width;
    delete node.height;
    node.fit = [PAGE_WIDTH, MAX_IMAGE_HEIGHT];
    node.alignment = "center";
    node.margin = [0, 6, 0, 10];
  }
  for (const key of ["stack", "columns", "ul", "ol", "text"]) {
    if (Array.isArray(node[key])) fitImages(node[key]);
  }
  if (node.table?.body) node.table.body.forEach((row) => fitImages(row));
  return node;
}

/**
 * Build a pdfmake document definition from a generated Markdown file. Chart
 * slots use the stored snapshot, or `drawPart` when the file has none yet;
 * the embedded data block and Mermaid source are left out.
 */
export async function markdownPdfDefinition({ markdown, title, resolveImage, drawPart, marked, DOMPurify, htmlToPdfmake, window }) {
  const prepared = await inlineImages(await pdfMarkdown(markdown, { drawPart }), resolveImage);
  const html = DOMPurify.sanitize(marked.parse(prepared, { gfm: true, breaks: true }));
  const content = htmlToPdfmake(html, {
    window,
    ignoreStyles: ["font-family"],
    defaultStyles: {
      h1: { fontSize: 18, bold: true, marginBottom: 6 },
      h2: { fontSize: 14, bold: true, marginTop: 10, marginBottom: 4 },
      h3: { fontSize: 12, bold: true, marginTop: 8, marginBottom: 4 },
      blockquote: { color: "#4b5563", italics: true },
      th: { bold: true, fillColor: "#f3f4f6" },
    },
  });
  return {
    info: { title: title || "Report" },
    pageSize: "A4",
    pageMargins: [40, 40, 40, 48],
    content: fitImages(content),
    footer: (page, pages) => ({ text: `${page} / ${pages}`, alignment: "right", margin: [0, 16, 40, 0], fontSize: 8, color: "#6b7280" }),
  };
}
